import React, { useState, useEffect, useRef } from 'react';
import { Award, TrendingUp, TrendingDown, Cpu, Sliders, Info, RefreshCw, ShoppingBag, AlertTriangle, CheckCircle, FileDown } from 'lucide-react';
import RevenueChart from './RevenueChart';
import { exportInvestorOnePager } from './onePager';

// Founder Finance · web Financials tab of the Executive Suite. 1:1 mirror of the
// mobile FinancialsTab: live company valuation (SaaS ARR multiple, number-roll),
// MRR line chart, Net-Profit cashflow waterfall, operational cost / ECU HUD, an
// investment modeler and recent transactions. Green = profit, red = cost.
const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', creamWarm: '#F4EEDE',
  lime: '#B4D330', gold: '#C9A85B', leafDeep: '#4E7A33', terracotta: '#C66B4A',
  text: '#2D241E', textMuted: '#5C665C', textSoft: '#7A6B57', white: '#FFFFFF',
  border: 'rgba(26,67,49,0.10)', borderStrong: 'rgba(26,67,49,0.18)', sageDeep: '#D9E1CB',
  goldTint: 'rgba(201,168,91,0.14)', leafTint: 'rgba(107,159,74,0.14)',
};
const PROFIT = C.leafDeep, COST = C.terracotta;
const money0 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const OF = { fontFamily: 'Outfit, sans-serif' };

function useRoll(target, dur = 950) {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current; const start = Date.now(); let raf;
    const tick = () => {
      const p = Math.min((Date.now() - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick); else fromRef.current = target;
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return val;
}

const cardCream = { background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, padding: 24, marginBottom: 20 };
const label = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.textMuted };
const rowBetween = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };

function FlowRow({ label: lbl, sub, amount, positive }) {
  const neg = amount < 0;
  return (
    <div style={{ ...rowBetween, padding: '8px 0' }}>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 14, fontWeight: 600, color: C.text, margin: 0 }}>{lbl}</p>
        <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>{sub}</p>
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: positive ? PROFIT : neg ? COST : C.text }}>
        {neg ? '-' : positive ? '+' : ''}{money0(Math.abs(amount))}
      </span>
    </div>
  );
}

function ModelStat({ label: lbl, value, sub, accent }) {
  return (
    <div style={{ flex: 1, background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14 }}>
      <p style={{ ...OF, fontSize: 22, lineHeight: '28px', color: accent || C.forest, margin: 0, fontWeight: 700 }}>{value}</p>
      <p style={{ fontSize: 12, fontWeight: 600, color: C.text, margin: '2px 0 0' }}>{lbl}</p>
      <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>{sub}</p>
    </div>
  );
}

export default function FinancialsTab({ data }) {
  const val = data?.valuation || {};
  const net = data?.net || {};
  const costs = data?.costs || {};
  const gross = data?.gross || {};
  const fees = data?.fees || {};
  const modeler = data?.modeler || {};
  const readiness = data?.readiness || {};

  const [mult, setMult] = useState(val.default_key || 'standard');
  const [spend, setSpend] = useState(1000);
  const valuation = val.values?.[mult] ?? 0;
  const rollVal = useRoll(valuation);
  const multEntries = Object.entries(val.multipliers || { conservative: 4, standard: 7, aggressive: 10 });
  const upM = (val.mom_growth_pct || 0) >= 0;

  // investment modeler
  const cac = modeler.assumed_cac || 22;
  const users = Math.floor(spend / cac);
  const addedMrr = Math.round(users * (modeler.arpu || 13));
  const newMrr = Math.round((modeler.current_mrr || 0) + addedMrr);
  const payback = addedMrr > 0 ? (spend / addedMrr).toFixed(1) : '—';

  if (!data) return null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => exportInvestorOnePager(data, mult)} data-testid="export-one-pager"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.forest, color: '#fff', fontWeight: 600, fontSize: 13, padding: '9px 16px', borderRadius: 999, border: 'none', cursor: 'pointer' }}>
          <FileDown className="w-4 h-4" /> Export investor one-pager
        </button>
      </div>
      {/* ---- Legacy HUD · Company valuation ---- */}
      <div style={{ borderRadius: 20, padding: 24, marginBottom: 20, border: '1px solid rgba(201,168,91,0.5)', overflow: 'hidden', position: 'relative', background: 'linear-gradient(135deg,#173C2C,#0F2D1F)' }} data-testid="financials-valuation">
        <div style={{ position: 'absolute', top: -80, right: -50, width: 220, height: 220, borderRadius: 999, background: 'rgba(201,168,91,0.18)' }} />
        <div style={{ position: 'relative' }}>
          <div style={rowBetween}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Award className="w-4 h-4" style={{ color: C.gold }} />
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: C.gold }}>ESTIMATED COMPANY VALUE</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 999, padding: '3px 9px', background: upM ? 'rgba(107,159,74,0.22)' : 'rgba(198,107,74,0.22)' }}>
              {upM ? <TrendingUp className="w-3 h-3" style={{ color: C.lime }} /> : <TrendingDown className="w-3 h-3" style={{ color: COST }} />}
              <span style={{ fontSize: 12, fontWeight: 600, color: upM ? C.lime : COST }}>{upM ? '+' : ''}{val.mom_growth_pct || 0}% / mo</span>
            </div>
          </div>
          <p style={{ ...OF, fontSize: 48, lineHeight: '54px', color: C.gold, margin: '6px 0 0', fontWeight: 700 }} data-testid="valuation-total">{money0(rollVal)}</p>
          <p style={{ fontSize: 13, color: 'rgba(250,246,236,0.65)', margin: 0 }}>{money0(val.arr)} ARR × {val.multipliers?.[mult]}× multiple</p>

          {/* multiplier toggle */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {multEntries.map(([k, m]) => {
              const a = mult === k;
              return (
                <button key={k} onClick={() => setMult(k)} data-testid={`valuation-mult-${k}`}
                  style={{ flex: 1, padding: '9px 0', borderRadius: 12, cursor: 'pointer', textTransform: 'capitalize', fontWeight: 600, fontSize: 12.5, border: `1px solid ${a ? C.gold : 'rgba(250,246,236,0.2)'}`, background: a ? C.gold : 'rgba(250,246,236,0.06)', color: a ? C.forestDeep : C.cream, transition: 'all 0.2s' }}>
                  {k} {m}×
                </button>
              );
            })}
          </div>

          {/* investor readiness mini-stats */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16, borderTop: '1px solid rgba(250,246,236,0.12)', paddingTop: 16 }}>
            {(readiness.factors || []).map((f) => (
              <div key={f.key} style={{ flex: 1, textAlign: 'center' }} data-testid={`readiness-${f.key}`}>
                <p style={{ fontSize: 15, fontWeight: 700, color: C.lime, margin: 0 }}>{f.score}</p>
                <p style={{ fontSize: 11, color: 'rgba(250,246,236,0.6)', margin: 0 }}>{(f.label || '').split(' ')[0]}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---- MRR line chart (dark, high-contrast) ---- */}
      <div style={{ background: C.forestDeep, borderRadius: 20, border: '1px solid rgba(180,211,48,0.2)', padding: 24, marginBottom: 20 }} data-testid="financials-mrr-chart">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.lime }}>MONTHLY RECURRING REVENUE</span>
            <p style={{ ...OF, fontSize: 32, color: C.cream, margin: '2px 0 0', fontWeight: 700 }}>{money2(gross.mrr)}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 12, color: 'rgba(250,246,236,0.6)', margin: 0 }}>Projected / yr</p>
            <p style={{ ...OF, fontSize: 22, fontWeight: 700, color: C.gold, margin: 0 }}>{money0(gross.arr)}</p>
          </div>
        </div>
        <RevenueChart series={data.series || []} projection={data.projection || []} />
        <p style={{ fontSize: 12, color: 'rgba(250,246,236,0.55)', marginTop: 6, marginBottom: 0 }}>Solid = actual · dashed = 6-month projection</p>
      </div>

      {/* ---- Net Profit cashflow waterfall ---- */}
      <div style={cardCream} data-testid="financials-cashflow">
        <p style={{ ...label, marginBottom: 8 }}>MONTHLY CASHFLOW</p>
        <FlowRow label="Gross revenue" sub={`MRR ${money0(gross.mrr)} + shop ${money0(gross.shop_30d)}`} amount={gross.total_monthly} positive />
        <FlowRow label="Platform costs" sub="Compute · storage · AI (modeled)" amount={-costs.total_platform} />
        <FlowRow label="Stripe fees" sub="2.9% + 30¢ / txn" amount={-fees.stripe} />
        <FlowRow label="App Store fees" sub="Stripe billing — 0 until IAP" amount={-fees.app_store} />
        <div style={{ ...rowBetween, marginTop: 8, paddingTop: 14, borderTop: `1.5px solid ${C.borderStrong}` }}>
          <div>
            <p style={{ fontSize: 15, fontWeight: 700, color: C.forest, margin: 0 }}>Net profit</p>
            <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>{net.margin_pct}% margin</p>
          </div>
          <span style={{ ...OF, fontSize: 30, fontWeight: 700, color: net.net_profit >= 0 ? PROFIT : COST }} data-testid="net-profit">
            {net.net_profit >= 0 ? '' : '-'}{money0(Math.abs(net.net_profit))}
          </span>
        </div>
        {net.burning ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(198,107,74,0.12)', borderRadius: 12, padding: 14, marginTop: 14 }}>
            <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: COST }} />
            <span style={{ fontSize: 13, color: COST }}>Burning — ~{net.runway_days} days runway at this rate.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.leafTint, borderRadius: 12, padding: 14, marginTop: 14 }}>
            <CheckCircle className="w-4 h-4 shrink-0" style={{ color: PROFIT }} />
            <span style={{ fontSize: 13, color: PROFIT }}>Profitable — every $1 of revenue keeps {net.margin_pct}¢.</span>
          </div>
        )}
      </div>

      {/* ---- Operational cost / ECU HUD ---- */}
      <div style={cardCream} data-testid="financials-cost-hud">
        <div style={rowBetween}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Cpu className="w-4 h-4" style={{ color: COST }} />
            <span style={label}>PLATFORM CREDITS · ECU (MODELED)</span>
          </div>
          <span style={{ fontSize: 12, color: C.textMuted }}>${costs.per_active_user}/user</span>
        </div>
        <div style={{ ...rowBetween, marginTop: 4 }}>
          <span style={{ ...OF, fontSize: 34, fontWeight: 700, color: COST }}>{costs.ecu_credits} <span style={{ fontSize: 12, color: C.textMuted, fontFamily: 'Manrope, sans-serif' }}>ECU/mo</span></span>
          <span style={{ fontSize: 12, color: C.textMuted }}>{money0(costs.ecu_mtd)} used month-to-date</span>
        </div>
        <div style={{ marginTop: 8 }}>
          {(costs.breakdown || []).map((b) => {
            const pct = costs.total_platform ? (b.amount / costs.total_platform) * 100 : 0;
            return (
              <div key={b.key} style={{ marginTop: 10 }}>
                <div style={rowBetween}>
                  <span style={{ fontSize: 13, color: C.text }}>{b.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{money2(b.amount)}</span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: C.sageDeep, overflow: 'hidden', marginTop: 5 }}>
                  <div style={{ height: 8, borderRadius: 4, background: COST, width: `${Math.max(pct, 3)}%`, transition: 'width 0.6s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---- Investment modeler ---- */}
      <div style={cardCream} data-testid="financials-modeler">
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Sliders className="w-4 h-4" style={{ color: C.forest }} />
          <span style={label}>INVESTMENT MODELER</span>
        </div>
        <p style={{ ...OF, fontSize: 22, fontWeight: 700, color: C.forest, margin: '4px 0 0' }}>{money0(spend)} marketing spend</p>
        <div style={{ padding: '14px 0' }}>
          <input type="range" min={250} max={5000} step={250} value={spend} onChange={(e) => setSpend(Number(e.target.value))}
            data-testid="modeler-slider" style={{ width: '100%', accentColor: C.forest, cursor: 'pointer' }} />
          <div style={rowBetween}>
            <span style={{ fontSize: 12, color: C.textMuted }}>$250</span>
            <span style={{ fontSize: 12, color: C.textMuted }}>$5000</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ModelStat label="New users" value={`${users}`} sub={`@ $${cac} CAC`} />
          <ModelStat label="Added MRR" value={money0(addedMrr)} sub={`+${money0(addedMrr * 12)}/yr`} accent={PROFIT} />
          <ModelStat label="New MRR" value={money0(newMrr)} sub={`payback ${payback}mo`} accent={C.gold} />
        </div>
      </div>

      {/* ---- Transaction logs ---- */}
      <div style={{ ...cardCream, paddingBottom: 10 }} data-testid="financials-transactions">
        <p style={{ ...label, marginBottom: 8 }}>RECENT TRANSACTIONS</p>
        {(data.transactions || []).slice(0, 8).map((t, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
            <div style={{ width: 28, height: 28, borderRadius: 14, display: 'grid', placeItems: 'center', background: t.type === 'shop' ? C.goldTint : C.leafTint }}>
              {t.type === 'shop' ? <ShoppingBag className="w-3 h-3" style={{ color: C.gold }} /> : <RefreshCw className="w-3 h-3" style={{ color: PROFIT }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: 0 }}>{t.label}</p>
              <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>#{t.ref} · {t.when ? new Date(t.when).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }) : ''}</p>
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: PROFIT }}>+{money2(t.amount)}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <Info className="w-3.5 h-3.5 shrink-0" style={{ color: C.textMuted, marginTop: 2 }} />
        <span style={{ fontSize: 12, color: C.textMuted }}>{data.assumptions_note}</span>
      </div>
    </div>
  );
}
